import type { ManualEntryStructuredData } from './manualEntryTypes';

interface PreviewPanelProps {
  programId: string;
  programName?: string;
  content: string;
  structuredData: ManualEntryStructuredData;
  sourceUrl: string;
}

export function PreviewPanel({ programId, programName, content, structuredData, sourceUrl }: PreviewPanelProps) {
  const hasStructuredData = structuredData && (
    structuredData.categories.length > 0 ||
    structuredData.deadlines.length > 0 ||
    structuredData.fees_prizes.length > 0 ||
    structuredData.requirements.eligibility.length > 0 ||
    structuredData.requirements.restrictions.length > 0 ||
    structuredData.requirements.submission_types.length > 0 ||
    structuredData.contacts.length > 0 ||
    structuredData.judging.criteria.length > 0 ||
    Boolean(structuredData.judging.process?.trim())
  );
  
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid #e5e7eb'
    }}>
      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
        Preview Snapshot Data
      </h3>
      
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Metadata</h4>
        <div style={{
          backgroundColor: '#f9fafb',
          borderRadius: '4px',
          padding: '12px',
          fontSize: '13px',
          fontFamily: 'monospace'
        }}>
          <div><strong>Program:</strong> {programName || '—'}</div>
          <div><strong>Program ID:</strong> {programId === 'pending' ? 'To be created' : programId}</div>
          <div><strong>Source URL:</strong> {sourceUrl || 'manual-entry'}</div>
          <div><strong>Entry Method:</strong> manual</div>
          <div><strong>Content Length:</strong> {content.length} characters</div>
          <div><strong>Has Structured Data:</strong> {hasStructuredData ? 'Yes' : 'No'}</div>
        </div>
      </div>

      {hasStructuredData && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
            Structured Data Summary
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {structuredData.categories && structuredData.categories.length > 0 && (
              <span style={{
                padding: '4px 8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                Categories: {structuredData.categories.length}
              </span>
            )}
            {structuredData.deadlines && structuredData.deadlines.length > 0 && (
              <span style={{
                padding: '4px 8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                Deadlines: {structuredData.deadlines.length}
              </span>
            )}
            {structuredData.fees_prizes && structuredData.fees_prizes.length > 0 && (
              <span style={{
                padding: '4px 8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                Fees/Prizes: {structuredData.fees_prizes.length}
              </span>
            )}
            {structuredData.requirements && (
              <span style={{
                padding: '4px 8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                Requirements: {[
                  structuredData.requirements.eligibility?.length || 0,
                  structuredData.requirements.restrictions?.length || 0,
                  structuredData.requirements.submission_types?.length || 0
                ].reduce((a, b) => a + b, 0)}
              </span>
            )}
            {structuredData.contacts && structuredData.contacts.length > 0 && (
              <span style={{
                padding: '4px 8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                Contacts: {structuredData.contacts.length}
              </span>
            )}
            {structuredData.judging && structuredData.judging.criteria && (
              <span style={{
                padding: '4px 8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                Judging: {structuredData.judging.criteria.length} criteria
              </span>
            )}
          </div>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Content Preview</h4>
        <div style={{
          backgroundColor: '#f9fafb',
          borderRadius: '4px',
          padding: '12px',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          <pre style={{
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            margin: 0
          }}>
            {content.substring(0, 1000)}
            {content.length > 1000 && '...'}
          </pre>
        </div>
      </div>

      {hasStructuredData && structuredData.categories && structuredData.categories.length > 0 && (
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Categories Detail</h4>
          <div style={{ display: 'grid', gap: '8px' }}>
            {structuredData.categories.slice(0, 5).map((cat, index) => (
              <div key={index} style={{
                backgroundColor: '#f9fafb',
                borderRadius: '4px',
                padding: '8px',
                fontSize: '13px'
              }}>
                <div style={{ fontWeight: '500' }}>{cat.display_name || cat.name}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {cat.name} {cat.bisac_code && `• ${cat.bisac_code}`}
                </div>
              </div>
            ))}
            {structuredData.categories.length > 5 && (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                ...and {structuredData.categories.length - 5} more categories
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
